/**
 * AdminFreeAccess — give an account free, full access (Stage 3, 29 Sep 2026).
 * For your own stations, interns, partners. Writes users/{uid}.accessGrant; the portal functions then add the
 * products to the account (and remove them when the grant ends; "no end date" = never ends).
 * Your own station: sign up through "Host a Station" with its own email, close the Stripe page without paying,
 * then give that account Station (+ anything else) here.
 */
import React, { useState } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, deleteField, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase-config';
import '../portal/portal-theme.css';

const PRODUCTS = [
  { key: 'station', label: '📡 Station' },
  { key: 'advertiser', label: '🐋 Advertiser' },
  { key: 'decoder', label: '🔬 Decoder' },
  { key: 'researcher', label: '🔬 Researcher (includes Decoder)' },
];
// the role decides which login screen / dashboards an account uses; only set when the account has none
const roleFor = (p) => (p.includes('station') && p.includes('advertiser') ? 'station_advertiser'
  : p.includes('station') ? 'station' : p.includes('advertiser') ? 'advertiser' : 'analyst');
const fmt = (t) => (t?.toDate ? t.toDate().toLocaleDateString() : '');


const AdminFreeAccess = ({ user }) => {
  const [email, setEmail] = useState('');
  const [found, setFound] = useState(null);           // { id, data }
  const [products, setProducts] = useState(['station']);
  const [noEnd, setNoEnd] = useState(true);
  const [until, setUntil] = useState('');
  const [note, setNote] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState(null);

  const find = async () => {
    setMsg(''); setFound(null);
    const q = await getDocs(query(collection(db, 'users'), where('email', '==', email.trim().toLowerCase())));
    const q2 = q.empty ? await getDocs(query(collection(db, 'users'), where('email', '==', email.trim()))) : q;
    if (q2.empty) { setMsg('No account with this email. The person must sign up first (or check the spelling).'); return; }
    const d = q2.docs[0];
    setFound({ id: d.id, data: d.data() });
    const g = d.data().accessGrant;
    if (g) { setProducts(g.products || []); setNoEnd(!g.until); setNote(g.note || ''); }
  };

  const toggle = (k) => setProducts((p) => {
    let n = p.includes(k) ? p.filter((x) => x !== k) : [...p, k];
    if (k === 'researcher' && n.includes('researcher') && !n.includes('decoder')) n = [...n, 'decoder'];
    return n;
  });

  const save = async () => {
    if (!found || !products.length) return;
    if (!noEnd && !until) { setMsg('Pick an end date or tick "No end date".'); return; }
    setBusy(true); setMsg('');
    try {
      const update = {
        accessGrant: {
          products: [...new Set(products)].sort(),
          until: noEnd ? null : Timestamp.fromDate(new Date(`${until}T23:59:59`)),
          grantedBy: user?.email || 'admin', grantedAt: Timestamp.now(), note: note.trim() || null,
        },
      };
      if (!found.data.role) update.role = roleFor(products);
      await updateDoc(doc(db, 'users', found.id), update);
      setMsg('Saved. The account gets its access within a few seconds (it may need to sign in again).');
    } catch (e) { setMsg(`Could not save: ${e.message}`); }
    setBusy(false);
  };

  const remove = async () => {
    if (!found || !window.confirm('Remove the free access from this account?')) return;
    setBusy(true);
    try {
      await updateDoc(doc(db, 'users', found.id), { accessGrant: deleteField() });
      setMsg('Free access removed.');
    } catch (e) { setMsg(`Could not remove: ${e.message}`); }
    setBusy(false);
  };

  const loadList = async () => {
    const q = await getDocs(query(collection(db, 'users'), where('accessGrant.grantedAt', '>', Timestamp.fromDate(new Date(0)))));
    setList(q.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  return (
    <div style={{ maxWidth: 760 }}>
      <h2 className="ct-title">🎁 Free access</h2>
      <p className="ct-muted">
        Give an account free, full access — your own stations, interns, partners. For your own station: sign up with
        "Host a Station" using the station's email, close the payment page, then give it Station here.
      </p>

      <div className="ct-card">
        <label className="ct-muted">Account email</label>
        <div className="ct-row" style={{ marginTop: 6 }}>
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="station1@cetalingua.com"
                 className="ct-input" style={{ flex: 1, marginBottom: 0 }} onKeyDown={(e) => e.key === 'Enter' && find()} />
          <button className="ct-btn primary" onClick={find}>Find</button>
        </div>
        {found && (
          <p className="ct-muted" style={{ marginTop: 10 }}>
            <strong>{found.data.companyName || found.data.displayName || found.data.email}</strong>
            {' · '}role: {found.data.role || '(none)'}
            {found.data.stationId ? ` · station ${found.data.stationId}` : ''}
            {' · '}now uses: {(found.data.products || []).join(', ') || 'nothing'}
            {found.data.accessGrant ? ` · free access until ${found.data.accessGrant.until ? fmt(found.data.accessGrant.until) : 'no end date'}` : ''}
          </p>
        )}
      </div>

      {found && (
        <div className="ct-card">
          <div style={{ display: 'grid', gap: 6 }}>
            {PRODUCTS.map((p) => (
              <label key={p.key} className="ct-check"><input type="checkbox" checked={products.includes(p.key)} onChange={() => toggle(p.key)} /> {p.label}</label>
            ))}
          </div>
          <div style={{ marginTop: 12 }}>
            <label className="ct-check"><input type="checkbox" checked={noEnd} onChange={(e) => setNoEnd(e.target.checked)} /> No end date</label>
            {!noEnd && <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className="ct-input" style={{ maxWidth: 200 }} />}
          </div>
          <div style={{ marginTop: 12 }}>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (e.g. Own station, Intern 2026)"
                   className="ct-input" />
          </div>
          <div className="ct-row" style={{ marginTop: 14 }}>
            <button className="ct-btn primary" disabled={busy || !products.length} onClick={save}>Save free access</button>
            {found.data.accessGrant && <button className="ct-btn" disabled={busy} onClick={remove}>Remove free access</button>}
          </div>
        </div>
      )}

      {msg && <p className="ct-msg">{msg}</p>}

      <div className="ct-card">
        <button className="ct-btn" onClick={loadList}>Show all accounts with free access</button>
        {list && (list.length ? (
          <table className="ct-table">
            <thead><tr><th align="left">Account</th><th align="left">Access</th><th align="left">Until</th><th align="left">Note</th></tr></thead>
            <tbody>{list.map((u) => (
              <tr key={u.id}><td>{u.email}</td><td>{(u.accessGrant.products || []).join(', ')}</td>
                <td>{u.accessGrant.until ? fmt(u.accessGrant.until) : 'no end date'}</td><td>{u.accessGrant.note || ''}</td></tr>
            ))}</tbody>
          </table>
        ) : <p className="ct-muted">None yet.</p>)}
      </div>
    </div>
  );
};

export default AdminFreeAccess;
